import {
  Container,
  Flex,
  SimpleGrid,
  Text,
  Box,
  useDisclosure,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Button,
} from '@chakra-ui/react';
import {ResponsiveImageType} from 'react-datocms';
import {useRef} from 'react';
import {useRouter} from 'next/router';
import {ListType} from '../../graphql/generated';
import {BenefitTierRecord} from '../../graphql/datocms-generated';
import {
  QuatitySelectorProps,
  useQuantitySelector,
} from '../../hooks/quantity-selector';
import {mixpanel} from '../../utils/mixpanel';
import {pixelTrackEvent} from '../../utils/facebook-pixel';
import {fireGtmDataLayerEvent, EventType} from '../../utils/gtm-data-layer';
import {BenefitCard} from './benefit-card';
import {OwnAPieceSoldOut} from './sold-out-state';
import {PiecesCalculator} from './pieces-calculator';
import {AuthenticityCard} from './authenticity-card';
import {MobilePiecesCalculator} from './mobile-pieces-calculator';
import {MarketplaceListing} from './marketplace-listings';
import {FAQs} from '../global/faq';
import {VALENTINES_DAY_MASCARA} from '../../shared/consts';

interface PurchasePanelProps {
  slug: string;
  color: string;
  background: string;
  status: string;
  assetId: string;
  benefits: BenefitTierRecord[];
  totalPieces: number;
  pricePerPiece: number;
  maxAssetsPerUser: number;
  company: string;
  companyPrefix: string;
  companyLogo?: ResponsiveImageType;
  marketplaceImage?: string;
  currencySymbol: string;
  currencyPostFix: string;
}

export const PurchasePanel: React.FC<PurchasePanelProps> = ({
  slug,
  color,
  background,
  status,
  assetId,
  benefits,
  totalPieces,
  pricePerPiece,
  maxAssetsPerUser,
  company,
  companyPrefix,
  companyLogo,
  marketplaceImage,
  currencySymbol,
  currencyPostFix,
}) => {
  const router = useRouter();
  const {isOpen, onOpen, onClose} = useDisclosure();
  const buyPiecesBtnRef = useRef(null);
  const giftTransferRef = useRef();
  const quatityProps: QuatitySelectorProps = useQuantitySelector(
    totalPieces,
    pricePerPiece,
    assetId,
    maxAssetsPerUser,
  );
  const addPieces = (piecesNumber: number) => {
    quatityProps.setQuantity(piecesNumber);
    mixpanel.track('Buy Intent', {
      quantity: quatityProps.quantity,
      item: assetId,
    });
  };

  const addPiecesAndOpen = (piecesNumber: number) => {
    addPieces(piecesNumber);
    onOpen();
  };

  const handleContinue = () => {
    mixpanel.track('Buy Intent', {
      quantity: quatityProps.quantity,
      item: assetId,
    });
    pixelTrackEvent('AddToCart');
    fireGtmDataLayerEvent(EventType.AddToCart);
    router.push(`/checkout/${slug}`);
  };

  return (
    <>
      <Container p={0} maxW="1040px">
        {benefits.length > 0 &&
          (status === 'OFFERING_OPEN' || status === 'OFFERING_END') && (
            <Flex
              flex={1}
              justifyContent="center"
              layerStyle="card"
              pt={{base: 10, md: 12}}
              pb={{base: 10}}
              px={{base: 6, md: 18}}
              mt={{base: '2px', md: 6}}
              display={{base: 'column', md: 'none'}}
              outline="1px solid red"
            >
              <Text textStyle="button" mb={6} textAlign="center">
                More Pieces means more benefits:
              </Text>
              <SimpleGrid spacing={4} columns={{base: 1, lg: 3}}>
                {benefits.map((benefit: BenefitTierRecord) => (
                  <BenefitCard
                    key={benefit.quantity}
                    quantity={benefit.quantity}
                    hoverAction={() => addPiecesAndOpen(benefit.quantity)}
                    textColor={benefit.textColor?.hex || ''}
                    coinColor={benefit.coinColor || ''}
                    iconSrc={benefit?.tierIcon?.url || ''}
                    benefits={benefit.benefitList}
                    imageSrc={benefit?.benefitImage?.responsiveImage?.src || ''}
                  />
                ))}
              </SimpleGrid>
            </Flex>
          )}
        {companyLogo && companyPrefix && (
          <Flex display={{md: 'none'}}>
            <AuthenticityCard
              background={background}
              color={color}
              authenticatorText={company}
              cardText={companyPrefix}
              companyLogo={companyLogo}
            />
          </Flex>
        )}

        <Box display={{base: 'none', md: 'inherit'}}>
          {(status === 'SOLD_OUT' ||
            status === 'OFFERING_PENDING' ||
            status === 'OFFERING_CLOSED') && (
            <OwnAPieceSoldOut
              displaySoldOutMessage={status === 'SOLD_OUT'}
              listType={
                assetId === 'edward.8.penny'
                  ? ListType.EDWARD_MARKETPLACE
                  : ListType.MAGENTA_MARKETPLACE
              }
            />
          )}
          {(status === 'OFFERING_OPEN' || status === 'OFFERING_END') && (
            <PiecesCalculator
              benefits={benefits}
              quatitySelectorProps={quatityProps}
              goToCheckout={handleContinue}
              addPieces={addPieces}
              maxPieces={maxAssetsPerUser}
              currencySymbol={currencySymbol}
              currencyPostFix={currencyPostFix}
              hasPartners={!!companyLogo && !!company}
            />
          )}
          {status !== 'MARKETPLACE' && company && (
            <Flex>
              <AuthenticityCard
                background={background}
                color={color}
                authenticatorText={company}
                cardText={companyPrefix}
                companyLogo={companyLogo}
              />
            </Flex>
          )}
        </Box>
        {status !== 'MARKETPLACE' && assetId !== VALENTINES_DAY_MASCARA && (
          <Box mt={{base: '96px', md: '160px'}}>
            <FAQs giftTransferRef={giftTransferRef} />
          </Box>
        )}
      </Container>
      {status === 'MARKETPLACE' && (
        <Box>
          <Box display={{base: 'none', lg: 'block'}}>
            <AuthenticityCard
              boxStyle="short"
              background={background}
              color={color}
              authenticatorText={company}
              cardText={companyPrefix}
              companyLogo={companyLogo}
            />
          </Box>

          <MarketplaceListing image={marketplaceImage} assetId={assetId} />

          <Box display={{base: 'block', lg: 'none'}}>
            <AuthenticityCard
              background={background}
              color={color}
              authenticatorText={company}
              cardText={companyPrefix}
              companyLogo={companyLogo}
            />
          </Box>
        </Box>
      )}
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        finalFocusRef={buyPiecesBtnRef}
        placement="bottom"
        autoFocus={false}
      >
        <DrawerOverlay />
        <DrawerContent boxShadow="0px -4px 4px rgba(0, 0, 0, 0.15)">
          <MobilePiecesCalculator
            isOpen={isOpen}
            quatitySelectorProps={quatityProps}
            goToCheckout={handleContinue}
            maxPieces={maxAssetsPerUser || 0}
            currencySymbol={currencySymbol}
            currencyPostFix={currencyPostFix}
            hasPartners={!!companyLogo && !!company}
          />
        </DrawerContent>
      </Drawer>
      {(status === 'OFFERING_OPEN' ||
        status === 'OFFERING_END' ||
        status === 'OFFERING_CLOSED' ||
        status === 'OFFERING_PENDING' ||
        status === 'SOLD_OUT') && (
        <Box
          position="fixed"
          bottom="0"
          left="0"
          width="100%"
          p="8px"
          pb="20px"
          background="white"
          zIndex="banner"
          borderTopLeftRadius="16px"
          borderTopRightRadius="16px"
          boxShadow="0px -4px 4px rgba(0, 0, 0, 0.15)"
          display={{base: 'block', md: 'none'}}
        >
          {(status === 'OFFERING_OPEN' || status === 'OFFERING_END') && (
            <Button
              ref={buyPiecesBtnRef}
              size="lg"
              colorScheme="aqua"
              px="64px"
              alignSelf="center"
              onClick={onOpen}
              width="100%"
            >
              Buy Pieces
            </Button>
          )}
          {(status === 'OFFERING_CLOSED' ||
            status === 'OFFERING_PENDING' ||
            status === 'SOLD_OUT') && (
            <OwnAPieceSoldOut
              displaySoldOutMessage={status === 'SOLD_OUT'}
              listType={
                assetId === 'edward.8.penny'
                  ? ListType.EDWARD_MARKETPLACE
                  : ListType.MAGENTA_MARKETPLACE
              }
            />
          )}
        </Box>
      )}
    </>
  );
};
